import React, { createContext, useContext, useState, ReactNode } from 'react';

interface OrderContextType {
  getActiveOrderId: () => number | null;
  setActiveOrderId: (orderId: number | null) => void;
  removeActiveOrderId: () => void;
  refreshOrders: () => void;
  registerRefreshCallback: (callback: () => void) => void;
  getOrderObject: () => any;
  setOrderObject: (order: any) => void;

}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const useOrder = (): OrderContextType => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};

interface OrderProviderProps {
  children: ReactNode;
}

export const OrderProvider: React.FC<OrderProviderProps> = ({ children }) => {
  // Initialize from localStorage if available
  const [activeOrderId, setActiveOrderIdState] = useState<number | null>(null);
  const [order, setOrder] = useState<any>(null);
  const [refreshCallback, setRefreshCallback] = useState<(() => void) | null>(null);

  // Use a ref to immediately track the latest order ID for synchronous access
  const activeOrderIdRef = React.useRef<number | null>(null);

  // Wrapper to also persist to localStorage
  const setActiveOrderId = (orderId: number | null) => {
    activeOrderIdRef.current = orderId; // Update ref immediately for sync access
    setActiveOrderIdState(orderId); // Update state for React rendering
  };

  const removeActiveOrderId = () => {
    activeOrderIdRef.current = null; // Update ref immediately
    setActiveOrderIdState(null); // Update state for React rendering
  } 

  const refreshOrders = () => {
    if (refreshCallback) {
      refreshCallback();
    }
  };

  const registerRefreshCallback = (callback: () => void) => {
    setRefreshCallback(() => callback);
  };

  const setOrderObject = (order: any) => {
    setOrder(order);
  }

  const getOrderObject = () => {
    return order;
  }

  const getActiveOrderId = () => {
    // Return the ref value for immediate/synchronous access
    return activeOrderIdRef.current;
  }

  return (
    <OrderContext.Provider value={{ getActiveOrderId, setActiveOrderId, refreshOrders, registerRefreshCallback, removeActiveOrderId, getOrderObject, setOrderObject }}>
      {children}
    </OrderContext.Provider>
  );
};
